<template>
  <div class="mc-table">
    <div v-if="(!isLoading && !isEmpty) || (isEmpty && search)" class="clearfix mb-3">
      <div class="d-flex justify-content-between">
        <slot name="button-filter" />
        <div class="float-end d-flex">
          <McButton
            v-if="selectedIdsLength"
            variant="secondary-red"
            :text="$t('global.delete')"
            icon="icon-outline_trash"
            class="mb-2 ms-auto"
            @click="showDeleteModal"
          />
          <b-dropdown
            variant="outline-primary"
            class="mb-2 ms-2"
            :disabled="isBusy || !selectedIdsLength"
          >
            <template #button-content>
              {{ `${$t('global.update')} Status` }}
              <i class="mdi mdi-chevron-down" />
            </template>
            <b-dropdown-item @click="showUpdateStatus('I')">
              {{ $t('global.active') }}
            </b-dropdown-item>
            <b-dropdown-item @click="showUpdateStatus('R')">
              {{ $t('global.repair') }}
            </b-dropdown-item>
            <b-dropdown-item @click="showUpdateStatus('A')">
              {{ $t('global.archive') }}
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>
    </div>
    <div v-if="(!isLoading && !isEmpty) || (isEmpty && search)" class="card">
      <div class="card-body">
        <b-row>
          <b-col md="4" class="mc-row-check-wrapper">
            <h6 v-if="selectedIdsLength" class="mc-row-check mt-0">
              <span class="badge btn-success">{{ selectedIdsLength }}</span>
              {{ $t('global.object.selectedRow') }}
              <a class="color-primary" @click="handleUnselectAll">
                {{ $t('global.action.deselect') }}
              </a>
            </h6>
          </b-col>
          <b-col md="8" class="flex-row-reverse mc-table-view-wrapper">
            <div class="mc-table-view d-flex flex-row-reverse">
              <b-input-group size="sm" class="mc-has-search" @click="focusSearch">
                <template #prepend>
                  <i class="ps-2 mc-icon icon-fill_search" />
                </template>
                <b-form-input
                  ref="searchInput"
                  :placeholder="$t('global.search') + '...'"
                  :value="search"
                  @input="handleSearchChange"
                />
                <template v-if="search" #append>
                  <button class="clear-search-btn" @click="handleClearSearch">
                    <i class="pe-2 mc-icon icon-outline_close" />
                  </button>
                </template>
              </b-input-group>

              <slot
                name="viewManager"
                :handleSetDataFields="handleSetDataFields"
                :dataFields="dataFields"
                :initialFields="initialFields"
                :dataColumns="dataColumns"
              />
            </div>
          </b-col>
        </b-row>
        <div class="table-responsive border border-1 mc-table-overflow">
          <b-table-simple>
            <b-thead>
              <b-tr>
                <b-th
                  v-for="field in computedTopHeaderFields"
                  :key="field.key"
                  :rowspan="field.rowspan || 1"
                  :colspan="field.colspan || 1"
                >
                  <div v-if="field.key == 'checkbox'" class="form-check" style="width: 5px">
                    <b-form-checkbox
                      v-model="selectedAll"
                      :disabled="isBusy || ids.length === 0"
                      @change="handleSelectAll"
                    />
                  </div>

                  <div
                    v-else
                    :class="{ 'text-center': field.isHeader }"
                    @click="handleSortChange(field)"
                  >
                    {{ field.label }}
                    <template v-if="field.isSortable">
                      <div
                        v-if="showSortIcon(field, 'asc')"
                        :class="
                          computedBottomHeaderFields.length > 0
                            ? 'sort-icon-up'
                            : 'single-sort-icon-up'
                        "
                      >
                        <i class="mc-icon icon-outline_chevron-up" />
                      </div>
                      <div
                        v-if="showSortIcon(field, 'desc')"
                        :class="
                          computedBottomHeaderFields.length > 0
                            ? 'sort-icon-down'
                            : 'single-sort-icon-down'
                        "
                      >
                        <i class="mc-icon icon-outline_chevron-down" />
                      </div>
                    </template>
                  </div>
                </b-th>
              </b-tr>
              <b-tr>
                <b-th
                  v-for="field in computedBottomHeaderFields"
                  :key="field.key"
                  :rowspan="field.rowspan || 1"
                  :colspan="field.colspan || 1"
                >
                  <div @click="handleSortChange(field)">
                    {{ field.label }}
                    <template v-if="field.isSortable">
                      <div v-if="showSortIcon(field, 'asc')" class="child-sort-icon-up">
                        <i class="mc-icon icon-outline_chevron-up" />
                      </div>
                      <div v-if="showSortIcon(field, 'desc')" class="child-sort-icon-down">
                        <i class="mc-icon icon-outline_chevron-down" />
                      </div>
                    </template>
                  </div>
                </b-th>
              </b-tr>
            </b-thead>
            <b-tbody>
              <template v-if="isBusy">
                <b-tr>
                  <b-td :colspan="computedBodyFields.length">
                    <div class="__busy">
                      <b-spinner class="align-middle mb-2" />
                      <strong>{{ $t('global.text.loading') }}...</strong>
                    </div>
                  </b-td>
                </b-tr>
              </template>
              <template v-else>
                <template v-if="items.length == 0">
                  <b-tr>
                    <b-td :colspan="computedBodyFields.length" class="text-center">
                      <strong>{{ $t('global.emptyTable') }}</strong>
                    </b-td>
                  </b-tr>
                </template>
                <template v-else>
                  <b-tr v-for="item in items" :key="item[trackBy]">
                    <b-td v-for="field in computedBodyFields" :key="field.key">
                      <div v-if="field.key == 'checkbox'">
                        <b-form-checkbox
                          :checked="selectedIds[item[trackBy]]"
                          :disabled="isBusy"
                          @change="(event) => handleSelect(event, item)"
                        />
                      </div>
                      <div v-else>
                        <template v-if="field.isRedirectable">
                          <div
                            class="fw-bold text-primary td__noellipsis clickable-field"
                            @click="handleClickRow(field, item)"
                          >
                            {{ get(item, field.key) }}
                          </div>
                        </template>
                        <template v-else>
                          <template v-if="field.key == 'status'">
                            <span :class="field.formatter(get(item, field.key)).class">
                              {{ $t(field.formatter(get(item, field.key)).text) }}
                            </span>
                          </template>
                          <template v-else-if="field.key == 'imei'">
                            {{
                              item.sourceMode == 'GPS' ? field.formatter(get(item, field.key)) : '-'
                            }}
                          </template>
                          <template v-else-if="field.key == 'driver1.email'">
                            {{
                              item.sourceMode == 'DriverApps'
                                ? field.formatter(get(item, field.key))
                                : '-'
                            }}
                          </template>
                          <template
                            v-else-if="
                              field.key == 'identificationSupport.face' ||
                              field.key == 'identificationSupport.rfid'
                            "
                          >
                            <template v-if="field.formatter">
                              <span>
                                <i :class="field.formatter(get(item, field.key)).icon"></i>
                                {{ $t(field.formatter(get(item, field.key)).text) }}
                              </span>
                            </template>
                            <template v-else>
                              {{ get(item, field.key) || '-' }}
                            </template>
                          </template>
                          <template v-else>
                            {{
                              field.formatter
                                ? field.formatter(get(item, field.key))
                                : get(item, field.key) || '-'
                            }}
                          </template>
                        </template>
                      </div>
                    </b-td>
                  </b-tr>
                </template>
              </template>
            </b-tbody>
          </b-table-simple>
        </div>
        <div class="d-flex flex-wrap flex-md-nowrap">
          <div class="mc-limit">
            <div class="__limit">
              {{ $t('global.text.displays') }}
              <b-form-select
                class="form-select form-select-sm __limit--dropdown"
                size="sm"
                :disabled="isBusy"
                :options="limitOptions"
                :value="limit"
                @input="handleLimitChange"
              />
              <div>{{ $t('global.text.data') }}</div>
            </div>

            <div class="__info">
              <strong>{{ startData }}</strong> - <strong>{{ endData }}</strong>
              {{ $t('global.object.of') }} <strong>{{ totalCount }}</strong>
              {{ $t('global.text.data') }}
            </div>
          </div>
          <div class="mc-pagination-container">
            <b-pagination
              :disabled="isBusy"
              :per-page="limit"
              :total-rows="totalCount"
              :value="page"
              @change="handlePageChange"
            />
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="isLoading">
      <div class="loading-container">
        <div class="__busy d-flex align-items-center justify-content-center">
          <b-spinner class="align-middle mb-2" />
        </div>
      </div>
    </div>
    <div v-else-if="isEmpty && !search" class="card">
      <div class="card-body">
        <slot name="emptyState" />
      </div>
    </div>
    <b-modal
      id="mc-modal-delete"
      ref="mc-modal-delete"
      class="mc-modal"
      :title="$t('global.action.delete')"
      size="md"
      centered
    >
      <div class="card-body p-2">
        <p>
          {{ $t('global.confirmDelete1') }} <strong>{{ selectedIdsLength }}</strong>
          {{ $t('global.confirmDelete2') }}
        </p>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex">
          <McButton
            variant="secondary"
            :text="$t('global.action.cancel')"
            class="me-2"
            @click="cancel()"
          />
          <McButton variant="primary" :text="$t('global.action.delete')" @click="handleDelete" />
        </div>
      </template>
    </b-modal>
    <b-modal
      id="modal-update-status"
      class="mc-modal"
      title="Ubah Status"
      body-class="p-3"
      size="md"
      centered
    >
      <div class="card-body mc-modal__filter p-2">
        <p>
          {{ $t('global.text.modal.changeStatus') }} <strong>{{ selectedIdsLength }}</strong>
          {{ $t('global.text.modal.changeStatusLength') }}
          <b v-if="status === 'I'">{{ $t('global.active') }} </b>
          <b v-if="status === 'R'"> {{ $t('global.repair') }} </b>
          <b v-if="status === 'A'">{{ $t('global.archive') }} </b> ?
        </p>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex">
          <McButton
            variant="secondary"
            :text="$t('global.action.cancel')"
            class="me-2"
            @click="cancel()"
          />
          <McButton variant="primary" :text="$t('global.yes')" @click="updateByStatus" />
        </div>
      </template>
    </b-modal>
  </div>
</template>
<script>
import McButton from '@/components/McButton'
import { debounce } from '@/helpers/debounce'
import get from 'lodash/get'
import cloneDeep from 'lodash/cloneDeep'
import flattenDeep from 'lodash/flattenDeep'
import { GET_VEHICLE_ID, VEHICLE_MUTATION_UPDATE } from '@/services/api/master/fleet'

export default {
  components: {
    McButton,
  },
  props: {
    fields: {
      type: Array,
      default: () => [],
    },
    apiClient: {
      type: Object,
      default: () => {},
    },
    trackBy: {
      type: String,
      default: 'id',
    },
    limitOptions: {
      type: Array,
      default: () => [10, 25, 50, 100],
    },
    hasFilter: {
      type: Boolean,
      default: false,
    },
    activeFilters: {
      type: Object,
      default: () => {},
    },
    customHandlers: {
      type: Object,
      default: () => {},
    },
    isEmpty: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      page: 1,
      limit: 10,
      totalCount: 0,
      search: '',
      isBusy: true,
      items: [],
      sorts: [],
      ids: [],
      dataFields: this.cloneFields(this.fields),
      dataColumns: this.cloneColumns(this.fields),
      selectedIds: {},
      selectedIdsLength: 0,
      selectedAll: false,
      status: '',
      updateFleet: [],
    }
  },
  computed: {
    locale() {
      return this.$i18n.locale
    },
    computedTopHeaderFields() {
      const fields = []
      this.dataFields.forEach((item) => {
        if (!item.shown) return

        const obj = Object.assign({}, item)
        if (item.fields) {
          delete obj.fields
        }
        fields.push(obj)
      })
      if (!fields.find((item) => item.key == 'checkbox')) {
        fields.unshift({
          key: 'checkbox',
          label: '',
          rowspan: 2,
        })
      }
      return fields
    },
    computedBottomHeaderFields() {
      const fields = []
      this.dataFields.forEach((item) => {
        if (!item.shown || !item.fields) return

        item.fields.forEach((el) => {
          if (!el.shown) return
          fields.push(el)
        })
      })

      return flattenDeep(fields)
    },
    computedBodyFields() {
      const fields = []
      this.dataFields.forEach((item) => {
        if (!item.shown) return

        if (item.fields) {
          const newFields = item.fields.filter((el) => el.shown)
          fields.push(cloneDeep(newFields))
        } else {
          if (!item.shown) return

          const obj = Object.assign({}, item)
          fields.push(obj)
        }
      })
      if (!fields.find((item) => item.key == 'checkbox')) {
        fields.unshift({
          key: 'checkbox',
          label: '',
          rowspan: 2,
        })
      }

      return flattenDeep(fields)
    },
    startData() {
      return this.limit * this.page - this.limit + 1
    },
    endData() {
      const _endData = this.limit * this.page
      return this.totalCount < _endData ? this.totalCount : _endData
    },
    initialFields() {
      return this.cloneFields(this.fields)
    },
  },
  watch: {
    locale() {
      this.dataFields = this.cloneFields(this.fields)
    },
    activeFilters() {
      this.getData({
        limit: this.limit,
        page: 1,
      })
    },
  },
  async mounted() {
    await this.getData({
      limit: this.limit,
      page: this.page,
    })
  },
  methods: {
    focusSearch() {
      this.$refs.searchInput.focus()
    },
    deepClone(fields, level) {
      const finalFields = []

      fields.forEach((field) => {
        if (level == 'parent') {
          finalFields.push(field)
        } else {
          if (Object.prototype.hasOwnProperty.call(field, 'children')) {
            finalFields.push(...field.children)
          } else {
            finalFields.push(field)
          }
        }
      })
      return finalFields
    },
    cloneFields(fields) {
      const finalFields = this.deepClone(fields, 'child')
      finalFields.forEach((item) => {
        if (item.shown === undefined) item.shown = true
      })
      return finalFields
    },
    cloneColumns(fields) {
      const finalFields = this.deepClone(fields, 'parent')
      finalFields.forEach((item) => {
        if (item.shown === undefined) item.shown = true
      })
      return finalFields
    },
    get,
    async getData(opts) {
      this.isBusy = true
      if (!this.apiClient?.get) {
        this.isBusy = false
        return
      }

      const filter = {
        pagination: {
          page: opts.page,
          show: opts.limit,
        },
        orderBy: opts.sorts,
        search: opts.search,
      }

      if (filter?.orderBy && !filter.orderBy.length) delete filter.orderBy
      if (filter?.search && !filter.search) delete filter.search

      const res = await this.apiClient.get(filter)
      if (!res) return
      this.items = this.itemProperty ? res.data[this.itemProperty] : res.data
      this.ids = this.items.map((data) => data[this.trackBy])
      this.page = res.pagination.page
      this.totalCount = res.pagination.total

      if (this.items.length === 0 && !this.search) {
        this.$emit('available-change', false)
      }

      this.$emit('refreshData', true)

      this.isBusy = false
    },
    stringSorts() {
      const arr = this.sorts.map((sort) => {
        const obj = {
          [sort.key]: sort.order,
        }
        return JSON.stringify(obj)
      })

      return arr
    },
    handleSearchChange: debounce(async function (_search) {
      this.search = _search
      const opts = {
        limit: this.limit,
        search: _search != '' ? _search : undefined,
        page: 1,
        sorts: this.stringSorts(),
      }
      await this.getData(opts)
      this.$refs['search-input-ref'].$el.focus()
    }, 500),
    handleClearSearch() {
      this.handleSearchChange(undefined)
    },
    async handleLimitChange(_limit) {
      this.limit = _limit

      await this.getData({
        limit: _limit,
        search: this.search != '' ? this.search : undefined,
        page: 1,
        sorts: this.stringSorts(),
      })
    },
    async handlePageChange(newPage) {
      const opts = {
        page: newPage,
        search: this.search != '' ? this.search : undefined,
        limit: this.limit,
        sorts: this.stringSorts(),
      }
      await this.getData(opts)
    },
    handleSetDataFields(fields) {
      this.dataFields = this.cloneFields(fields)
      this.dataColumns = this.cloneColumns(fields)
    },
    showSortIcon(data, order) {
      const currentColumnSort = this.sorts.find((sort) => sort.key === data.key)
      if (!currentColumnSort) {
        return true
      }

      return currentColumnSort.key === data.key && currentColumnSort.order === order
    },
    async handleSortChange(data) {
      // If this function is called without data params,
      // remove sorting (revert to default)
      if (!data) {
        this.sorts = []
        return
      }

      // If field not sortable or If currently editing a field, disable sort
      if (!data?.isSortable) return

      // Get index of current column sort, if any
      const currentColumnSortIndex = this.sorts.findIndex((sort) => sort.key === data.key)
      // Get the sorting data (key, order)
      const currentColumnSort = this.sorts[currentColumnSortIndex]
      let newSorts = [...this.sorts]
      // This switch case is used to toggle between these conditions:
      // default - asc - desc - default (repeat)
      switch (currentColumnSort?.order) {
        case 'asc':
          newSorts[currentColumnSortIndex] = {
            key: currentColumnSort.key,
            order: 'desc',
          }
          break

        case 'desc':
          newSorts.splice(currentColumnSortIndex, 1)
          break

        default:
          // If it have single sort, remove all other sorts
          if (this.singleSort) newSorts = []
          newSorts.push({ key: data.key, order: 'asc' })
          break
      }

      this.sorts = [...newSorts]

      await this.$nextTick()

      return this.getData({
        limit: this.limit,
        page: 1,
        search: this.search != '' ? this.search : undefined,
        sorts: this.stringSorts(),
      })
    },
    handleSelect(checked, data) {
      this.selectedIds = { ...this.selectedIds, [data[this.trackBy]]: checked }
      if (checked) {
        this.selectedIdsLength += 1
      } else {
        this.selectedIdsLength -= 1
      }
      let flag = true
      const selectedKeys = Object.keys(this.selectedIds)
        .filter((key) => this.selectedIds[key])
        .map(Number)
      for (const iterator of this.ids) {
        let index = selectedKeys.indexOf(iterator)
        if (index == -1) {
          flag = false
          break
        }
      }
      if (flag) this.selectedAll = true
      else this.selectedAll = false

      this.$emit('totalSelected', this.selectedIdsLength)
      this.$emit('handleSelectValue', this.selectedIds)
    },
    handleSelectAll(event) {
      if (event) {
        this.selectedIds = Object.fromEntries(this.ids.map((key) => [key, true]))
        this.$emit('handleSelectValue', this.selectedIds)
        this.selectedIdsLength = this.ids.length
        this.selectedAll = true
      } else {
        this.handleUnselectAll()
      }
    },
    handleUnselectAll() {
      this.selectedIds = {}
      this.$emit('handleSelectValue', {})
      this.selectedAll = false
      this.selectedIdsLength = 0
    },
    handleClickRow(field, item) {
      if (field.isRedirectable && this.customHandlers?.handleClickRow)
        this.customHandlers.handleClickRow(item)
    },
    async showDeleteModal() {
      const selectedKeys = Object.keys(this.selectedIds)
        .filter((key) => this.selectedIds[key])
        .map(Number)
      let data = []
      this.isBusy = true
      for (const id of selectedKeys) {
        await this.$apollo
          .query({
            query: GET_VEHICLE_ID,
            variables: {
              filter: {
                id: parseInt(id),
              },
            },
            fetchPolicy: 'no-cache',
          })
          .then((res) => {
            if (res.data.vehicle.isSuccessful) {
              data.push(res.data.vehicle.vehicle)
            }
          })
      }
      let canDelete = false
      for (const iterator of data) {
        if (!iterator.imei && iterator.status == 'A') {
          canDelete = true
        } else {
          canDelete = false
          break
        }
      }
      this.isBusy = false
      if (canDelete) {
        this.$refs['mc-modal-delete'].show()
      } else {
        this.$mcToast.error({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                tag: 'h6',
                text: this.$t('master.list.fleet.errorDeleteTitle'),
                class: 'text-danger',
              },
              { tag: 'p', text: this.$t('master.list.fleet.errorDelete') },
            ],
          },
        })
      }
    },
    async handleDelete() {
      const ids = Object.keys(this.selectedIds)
        .filter((key) => this.selectedIds[key])
        .map(Number)
      if (this.search) this.handleClearSearch()
      this.handleUnselectAll()
      this.$refs['mc-modal-delete'].hide()
      const res = await this.apiClient.delete(ids)
      const requestData = {
        limit: this.limit,
        page: 1,
        sorts: this.stringSorts(),
      }
      setTimeout(() => {
        if (res) {
          this.getData(requestData)
          this.$mcToast.success(this.$t('global.alert.text.delete'))
        } else {
          this.getData(requestData)
          this.$mcToast.error({
            component: 'McToastDynamicBody',
            props: {
              contents: [
                {
                  tag: 'h6',
                  text: this.$t('maintenance.alert.deleteFailed'),
                },
                {
                  tag: 'p',
                  text: this.$t('global.alert.text.failedDelete'),
                },
              ],
            },
          })
        }
      }, 300)
    },
    async showUpdateStatus(status) {
      this.status = status
      this.$bvModal.show('modal-update-status')
    },
    async updateByStatus(bvModalEvent) {
      this.isBusy = true
      bvModalEvent.preventDefault()
      const ids = Object.keys(this.selectedIds)
        .filter((key) => this.selectedIds[key])
        .map(Number)
      this.$bvModal.hide('modal-update-status')

      for (const iterator of ids) {
        await this.$apollo
          .query({
            query: GET_VEHICLE_ID,
            variables: {
              filter: {
                id: parseInt(iterator),
              },
            },
            fetchPolicy: 'no-cache',
          })
          .then((res) => {
            let isSuccessful = res.data.vehicle.isSuccessful
            if (isSuccessful) {
              this.updateFleet.push(res.data.vehicle.vehicle)
            }
          })
      }

      let promises = []

      for (const element of this.updateFleet) {
        promises.push(
          await this.$apollo.mutate({
            mutation: VEHICLE_MUTATION_UPDATE,
            variables: {
              input: {
                oldData: {
                  licensePlate: element.licensePlate,
                  status: element.status,
                },
                newData: {
                  licensePlate: element.licensePlate,
                  status: this.status,
                },
                id: element.id,
              },
            },
          })
        )
      }

      await Promise.all(promises)
        .then((results) => {
          this.updateFleet = []

          const isErrorService = results.some(
            (res) =>
              !res.data.updateVehicle.isSuccessful &&
              res.data.updateVehicle.message?.includes('Vehicle connected to Running') &&
              res.data.updateVehicle.message?.includes('Service Schedule')
          )
          const allSuccessful = results.every((res) => res.data.updateVehicle.isSuccessful)

          if (allSuccessful) {
            this.$mcToast.success(this.$t('global.alert.text.changed'))
          } else if (isErrorService) {
            this.$mcToast.error(this.$t('master.list.fleet.errorConnectedService'))
          } else {
            this.$mcToast.error(this.$t('global.alert.text.failedSaved'))
          }

          this.handleUnselectAll()
          this.getData({
            limit: this.limit,
            page: 1,
            sorts: this.stringSorts(),
          })
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.log(error)
        })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './style.scss';
</style>
